import I18n from 'I18n';
export const momentWithTimezone = (timezone, timestamp, useUserTz) => {
  if (timezone) {
    return I18n.moment(timestamp).tz(timezone);
  }
  if (useUserTz) {
    return I18n.moment.userTz(timestamp);
  }
  return I18n.moment.portalTz(timestamp);
};
let cachedMoments = {};
export const getCachedMoment = (timestamp, timezone, useUserTz = false) => {
  if (typeof timestamp !== 'number' && I18n.moment.isMoment(timestamp)) {
    return timestamp;
  }
  const startOfWeek = I18n.moment(timestamp).weekday(0).day();
  const key = `${timezone}:${timestamp}:${useUserTz}-${startOfWeek}`;
  if (!cachedMoments[key]) {
    cachedMoments[key] = momentWithTimezone(timezone, timestamp, useUserTz);
  }
  return cachedMoments[key];
};
export function clearCachedMoments() {
  cachedMoments = {};
}
export function now() {
  return Date.now();
}

/* GETTERS */
export function getMomentMonth(year, month, date = 1, timezone) {
  return momentWithTimezone(timezone).year(year).month(month).date(date);
}
export function getMomentForWeek(year, dayOfYear, day, timezone) {
  return momentWithTimezone(timezone).year(year).dayOfYear(dayOfYear).weekday(day);
}
export function getMomentForDay(year, dayOfYear, timezone) {
  return momentWithTimezone(timezone).year(year).dayOfYear(dayOfYear);
}
export function getDaysInMonth(year, monthNumber) {
  const momentMonth = getMomentMonth(year, monthNumber);
  return momentMonth.daysInMonth();
}
export function getFirstOffset(year, monthNumber, timezone) {
  const momentMonth = getMomentMonth(year, monthNumber, 1, timezone);
  return momentMonth.weekday();
}
export function getDisplayStartDate(year, monthNumber, timezone) {
  return getMomentMonth(year, monthNumber, 1, timezone).subtract(getFirstOffset(year, monthNumber, timezone), 'days');
}
export function getDayName(dayOfWeek) {
  return I18n.moment.weekdays(true, dayOfWeek);
}
export function getCompactDayName(dayOfWeek) {
  return I18n.moment.weekdaysMin(true, dayOfWeek);
}
export function getDayNameWithLocale(dayOfWeek, locale) {
  return I18n.moment().weekday(dayOfWeek).locale(locale).format('dd');
}
const WEEKENDS = [6, 7];
export function isWeekday(dayOfWeek, locale = 'en') {
  return !WEEKENDS.includes(momentWithTimezone().locale(locale).weekday(dayOfWeek).isoWeekday());
}

/**
 *
 * @param hideWeekends Whether weekends should be filtered from the array of day numbers
 * @param locale
 * @returns An array of day numbers, if hide weekends is true those days will be filtered out,
 *          if the we starts on Sunday, the first value will be 1
 */
export const getWeekdaysRange = (hideWeekends = false, locale = 'en') => {
  return [...Array(7).keys()].filter(dayOfWeek => !hideWeekends || isWeekday(dayOfWeek, locale));
};
export const getWeekLowerBound = (year, dayOfYear, hideWeekends = false, timezone, locale = 'en') => {
  const weekdaysRange = getWeekdaysRange(hideWeekends, locale);
  return momentWithTimezone(timezone).locale(locale).year(year).dayOfYear(dayOfYear).weekday(weekdaysRange[0]).startOf('day');
};
export const getWeekUpperBound = (year, dayOfYear, hideWeekends = false, timezone, locale = 'en') => {
  const weekdaysRange = getWeekdaysRange(hideWeekends, locale);
  return momentWithTimezone(timezone).locale(locale).year(year).dayOfYear(dayOfYear).weekday(weekdaysRange[weekdaysRange.length - 1]).endOf('day');
};
export function getFormattedHour(hour, timezone) {
  return momentWithTimezone(timezone).hour(hour).minute(0).format('LT');
}
export function getDayOfWeek(time, timezone) {
  return getCachedMoment(time, timezone).weekday();
}
export function getHour(time, timezone) {
  return getCachedMoment(time, timezone).hour();
}
export function getMinute(time, timezone) {
  return getCachedMoment(time, timezone).minute();
}
export function diff(timeA, timeB, magnitude, exact = false) {
  return getCachedMoment(timeA).diff(getCachedMoment(timeB), magnitude, exact);
}
export function getDisplayMonthRows(year, month) {
  const momentMonth = getMomentMonth(year, month);
  const startDisplayDate = momentMonth.clone().startOf('month').startOf('week');
  const endDisplayDate = momentMonth.clone().endOf('month').endOf('week');
  const displayRows = Math.ceil(endDisplayDate.diff(startDisplayDate, 'week', true));
  return displayRows;
}

/* CHECKERS */
export function isBetween(time, {
  start,
  end
}, timezone, magnitude) {
  return getCachedMoment(time, timezone).isBetween(start, end, magnitude);
}
export function isSame(timeA, timeB, timezone, magnitude) {
  return getCachedMoment(timeA, timezone).isSame(getCachedMoment(timeB, timezone), magnitude);
}
export function isBetweenInclusive(time, {
  start,
  end
}, timezone, magnitude) {
  return getCachedMoment(time, timezone).isBetween(start, end, magnitude) || isSame(time, start, timezone, magnitude) || isSame(time, end, timezone, magnitude);
}
export function isBefore(timeA, timeB, timezone, magnitude) {
  return getCachedMoment(timeA, timezone).isBefore(getCachedMoment(timeB, timezone), magnitude);
}
export function isAfter(timeA, timeB, timezone, magnitude) {
  return getCachedMoment(timeA, timezone).isAfter(getCachedMoment(timeB, timezone), magnitude);
}
export function compare(...args) {
  if (isAfter(...args)) return 1;
  if (isBefore(...args)) return -1;
  return 0;
}
const isComparableComponentEvent = child => {
  return typeof child === 'object' && 'props' in child && 'start' in child.props && 'end' in child.props;
};
export function eventsOverlap(event, otherEvent) {
  if (!isComparableComponentEvent(event) || !isComparableComponentEvent(otherEvent)) {
    return false;
  }
  if (event.props.start <= otherEvent.props.start && event.props.end > otherEvent.props.start) {
    return true;
  }
  return otherEvent.props.start <= event.props.start && otherEvent.props.end > event.props.start;
}
export const isSunday = date => I18n.moment.isMoment(date) ? date.day() === 0 : new Date(date).getDay() === 0;

/* SETTERS */

export function add(startTime, value, magnitude) {
  return momentWithTimezone(undefined, startTime).add(value, magnitude);
}
export function subtract(startTime, value, magnitude) {
  return momentWithTimezone(undefined, startTime).subtract(value, magnitude);
}